import type { ObjectId } from 'mongodb';

import type { Meta } from './Entities';

export interface FundDocument {
  id: ObjectId;
  type: FundType;
  key: string; // antillean-taxEquity
  description: string;
  netSuiteSubsidiaryId: number;
  concordLenderCode: string | null; // i.e AS2 of BS1 etc. Must ask Concord for this and they may have to create it in their system
  tranches?: Array<Tranche>;
  history?: Array<FundHistory>;
  commitmentExpirationDate: Date;
  fundCompletionDate: Date;
  meta?: Meta;
}

export interface FundHistory {
  date: Date;
  event: FundEvent;
  user: string;
  tranche: Tranche;
  accountCounter?: number;
}

export enum FundType {
  taxEquity = 'taxEquity',
  debt = 'debt',
  untrancheableAsset = 'untrancheableAsset',
  jointUntrancheableAsset = 'jointUntrancheableAsset',
  hvacJv = 'hvacJv',
}

export enum FundEvent {
  updateTranche = 'updateTranche',
  createTranche = 'createTranche',
  addAccount = 'addAccount',
  reassignAccount = 'reassignAccount',
  removeAccount = 'removeAccount',
  trancheClosed = 'trancheClosed',
  documentPull = 'documentPull',
}

export enum TrancheStatus {
  open = 'open',
  closed = 'closed',
}

export interface Tranche {
  name: string;
  submittedDate: Date;
  trancheDate?: Date;
  trancheStatus: TrancheStatus;
  trancheDataDownloadDate?: Date;
  trancheClosedDate?: Date;
}

export interface TrancheSummary {
  name: string;
  submittedDate: Date;
  trancheDate?: Date;
  accountCount: number;
  trancheDataDownloadDate?: Date;
}

export interface FundListItem {
  fundId: ObjectId;
  fundKey: string;
  fundType: FundType;
  description: string;
  netSuiteSubsidiaryId: number;
  concordLenderCode: string;
  commitmentExpirationDate: Date;
  fundCompletionDate: Date;
  createdDate?: Date;
  trancheCount: number;
}

export interface FundSummary {
  fundKey: string;
  fundType: FundType;
  description: string;
  netSuiteSubsidiaryId: number;
  concordLenderCode: string;
  commitmentExpirationDate: Date;
  fundCompletionDate: Date;
  createdDate: Date;
  tranches: Array<TrancheSummary>;
}

export enum TranchedAccountSearchField {
  address1 = 'address.address1',
  address2 = 'address.address2',
  addressCity = 'address.city',
  addressState = 'address.state',
  addressZIP = 'address.zip',
  applicantEmail = 'applicants.email',
  applicantFirstName = 'applicants.firstName',
  applicantLastName = 'applicants.lastName',
}

export interface UpdateTrancheRequestBody {
  trancheDataDownloadDate?: string;
  submittedDate: string;
  trancheDate?: string;
  isClosed?: boolean;
}

export const FundTypesLabels: Record<FundType, string> = {
  [FundType.taxEquity]: 'Tax Equity',
  [FundType.hvacJv]: 'HVAC JV',
  [FundType.untrancheableAsset]: 'Untrancheable Asset',
  [FundType.debt]: 'Debt',
  [FundType.jointUntrancheableAsset]: 'Joint Untrancheable Asset',
};
